'use client';

import {
	filterAccordionContentCSS,
	filterAccordionIconCSS,
	filterAccordionSelectedCountCSS,
	filterAccordionSummaryCSS,
} from '@/sections/filter/filter.css';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { stack } from '@/uiPrimitives/layout/stack';
import { icon } from '@/uiPrimitives/media/icon';
import {
	FilterDetails,
	FilterSelectedOptionCount,
} from '@/sections/filter/FilterDetails';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { checkboxCSS } from '@/uiComposites/form/checkbox.css';
import { useId } from 'react';
import { FilterClearToggle } from '@/sections/filter/FilterClearToggle';
import { FilterCheckbox } from '@/sections/filter/FilterCheckbox';
import {
	filterGroups,
	SubsidyFilterOption,
	SubsidyFilters,
	subsidyFilterQueryKeys,
} from './subsidyFilterKeys';

export const FilterOptions = ({
	options,
	groupKey,
	selectAllText,
	className,
}: {
	options: Array<SubsidyFilterOption>;
	groupKey: string;
	selectAllText?: string;
	className?: string;
}) => {
	const baseID = useId();
	return (
		<div className={classes(stack({ gap: '2 | h2' }), className)}>
			<FilterClearToggle groupKey={groupKey}>{selectAllText}</FilterClearToggle>

			{options.map((option) => {
				const id = `${baseID}-${option.key}`;

				return (
					<div
						key={option.key}
						className={classes(
							shelf({
								gridTemplateColumns: 'auto minmax(0, 1fr)',
								alignItems: 'start',
							}),
						)}
						{...addTestSelector(`uiFilterCheckbox-${option.key}`)}
					>
						<span
							className={classes(
								occupy({
									inlineSize: indentSize,
									blockSize: 'capSize',
									justifyItems: 'start',
								}),
							)}
						>
							<FilterCheckbox
								id={id}
								name={id}
								optionKey={option.key}
								groupKey={groupKey}
								className={classes(checkboxCSS)}
							/>
						</span>
						<label
							htmlFor={id}
							className={classes(
								textBlock({
									color: 'positive',
								}),
							)}
						>
							{option.name}
						</label>
					</div>
				);
			})}
		</div>
	);
};

export const SubsidyFilter = ({
	filters,
	className,
	['data-ui-test']: dataUITest,
}: {
	filters: SubsidyFilters;
	className?: string;
	['data-ui-test']?: string;
}) => {
	const hasVisibleFilterOptions = filterGroups.some(
		(filterGroup) => filters[filterGroup.key].length > 0,
	);

	return (
		<div
			className={classes(className, stack({ gap: '5 | betweenParagraphs' }))}
		>
			{hasVisibleFilterOptions && (
				<div
					role="group"
					className={stack({ gap: '2 | h2' })}
					{...addTestSelector(dataUITest)}
				>
					<div
						className={classes(
							textStyle({
								capSize: '-1 | small',
							}),
							box({
								borderBlockStartStyle: 'solid',
								borderColor: 'positiveMuted',
								borderWidth: '1px',
							}),
						)}
					>
						{filterGroups.map((filterGroup) => {
							const options = filters[filterGroup.key];

							if (options.length === 0) {
								return null;
							}

							return (
								<FilterDetails
									key={filterGroup.key}
									groupKey={subsidyFilterQueryKeys[filterGroup.queryKeyRef]}
									className={classes(
										box({
											borderBlockEndStyle: 'solid',
											borderColor: 'positiveMuted',
											borderWidth: '1px',
										}),
									)}
									{...addTestSelector(`uiFilter-${filterGroup.key}`)}
								>
									<summary
										className={classes(
											shelf({
												gridTemplateColumns: 'minmax(0, 1fr) auto',
											}),
											box({
												paddingBlock: '1 | h3',
											}),
											textStyle({
												fontWeight: 'bold',
												color: 'positive',
											}),
										)}
									>
										<span
											className={classes(
												textBlock({
													textWrap: 'balance',
												}),
												filterAccordionSummaryCSS,
											)}
										>
											{filterGroup.label}
											<FilterSelectedOptionCount
												className={filterAccordionSelectedCountCSS}
												groupKey={
													subsidyFilterQueryKeys[filterGroup.queryKeyRef]
												}
											/>
										</span>
										<span
											className={classes(
												occupy({
													inlineSize: indentSize,
													blockSize: 'capSize',
													justifyItems: 'end',
													alignItems: 'end',
												}),
											)}
										>
											<i
												className={classes(
													icon({
														icon: chevronIcon,
														adjustIcon: 'rotate90deg',
														blockSize: '1ex',
													}),
													filterAccordionIconCSS,
												)}
											/>
										</span>
									</summary>
									<FilterOptions
										options={options}
										className={classes(filterAccordionContentCSS)}
										groupKey={subsidyFilterQueryKeys[filterGroup.queryKeyRef]}
										selectAllText={filterGroup.selectAllText}
									/>
								</FilterDetails>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};
